import type { FC } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useIntercom } from "@/shared/providers/IntercomProvider";

import holdingId from "@/shared/assets/illustrations/holding-id.svg";

export type IdCheckErrorType = "not_id" | "not_their_id";

const ERROR_CONTENT: Record<IdCheckErrorType, { header: string; message: string }> = {
  not_id: {
    header: "Let's try that again",
    message:
      "We couldn't read the ID you uploaded. This usually happens when the photo is blurry, cropped, or has glare across it.",
  },
  not_their_id: {
    header: "Let's use your ID here",
    message:
      "It seems like the ID you uploaded uses a different name. To verify it's really you setting up the account, we need an ID that belongs to you.",
  },
};

interface IdCheckErrorModalProps {
  open: boolean;
  type: IdCheckErrorType | null;
  onClose: () => void;
}

export const IdCheckErrorModal: FC<IdCheckErrorModalProps> = ({ open, type, onClose }) => {
  const { openSidePanel } = useIntercom();
  const content = type ? ERROR_CONTENT[type] : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex justify-center pb-2">
            <img src={holdingId} alt="Hand holding ID" className="h-25 w-auto" />
          </div>
          <DialogTitle className="text-center">{content?.header}</DialogTitle>
        </DialogHeader>
        <DialogBody className="-mt-4">
          <p className="text-center text-secondary">{content?.message}</p>
        </DialogBody>
        <div className="flex flex-col gap-3 mt-1">
          <Button variant="brand" rounded className="w-full" onClick={onClose}>
            Upload again
          </Button>
          <Button
            variant="link"
            size="sm"
            className="w-full py-0 -my-1 text-secondary"
            onClick={openSidePanel}
          >
            Get help
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
